import React, { useEffect } from "react";
import {
  Modal,
  Form,
  InputNumber,
  Select,
  Radio,
  Button,
  Row,
  Col,
  App,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchClients } from "../clientsSlice";
import { getDays } from "../../../lib/industries";
import { useAddNewNavigation } from "../../../hooks/useAddNewNavigation";
import { useRestoreDraft } from "../../../hooks/useRestoreDraft";
import { PlusOutlined } from "@ant-design/icons";
import { postData } from "../../../apiservice/apis";

const { Option } = Select;

const ClientLeavePolicyModal = ({ open, onClose, edit }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const goToAddNew = useAddNewNavigation();
  const { message } = App.useApp();

  const { clients } = useSelector((s) => s.clientsSuite);
  const authInfo = useSelector((s) => s.auth?.auth_info);
  const isIndia = authInfo?.country === "India";
  const days = getDays(); // ✅ pulled from lib
  useRestoreDraft(form, "clientLeavePolicyModal");

  useEffect(() => {
    if (!clients?.length) dispatch(fetchClients());

    if (edit) {
      // parse week_end_days from backend (could be array or string)
      const parsedWeekend = Array.isArray(edit.week_end_days)
        ? edit.week_end_days
        : typeof edit.week_end_days === "string"
        ? edit.week_end_days
            .replace(/[[\]\s]/g, "")
            .split(",")
            .map((n) => parseInt(n.trim()))
        : [];

      const leaveTypeLabel =
        edit.annual_leave_type === 1 ? "Calendar Days" : "Working Days";

      form.setFieldsValue({
        client_id: edit.id,
        annual_leave_type: leaveTypeLabel,
        annual_leave_days: Number(edit.annual_leave_days),
        sick_leave_days: Number(edit.sick_leave_days) || 0,
        flexi_leave_days: Number(edit.flexi_leave_days) || 0,
        weekend_days: days
          .filter((d) => parsedWeekend.includes(d.value))
          .map((d) => d.label), // ✅ show multiple correctly
        should_reset: edit.should_reset_accrued_leave || "no",
        carry_forward: edit.leaves_carry_forward
          ? Number(edit.leaves_carry_forward)
          : 0,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        annual_leave_type: "Calendar Days",
        annual_leave_days: 30,
        sick_leave_days: 0,
        flexi_leave_days: 0,
        should_reset: "yes",
        carry_forward: 0,
        weekend_days: ["Sunday", "Saturday"],
      });
    }
  }, [open, edit]);

  const onFinish = async (values) => {
    try {
      // Convert label → numeric day values
      const weekendNumeric = days
        .filter((d) => values.weekend_days.includes(d.label))
        .map((d) => d.value);

      // Map leave type to old numeric format (1 = Calendar, 2 = Working)
      const leaveTypeValue =
        values.annual_leave_type === "Calendar Days" ? 1 : 2;

      const payload = {
        action: edit ? "update" : "create",
        id: edit?.id || 0,
        client_id: values.client_id,
        annual_leave_type: leaveTypeValue, // ✅ numeric
        annual_leave_days: Number(values.annual_leave_days),
        sick_leave_days: Number(values.sick_leave_days) || 0,
        flexi_leave_days: Number(values.flexi_leave_days) || 0,
        week_end_days: weekendNumeric, // ✅ array
        should_reset_accrued_leave: values.should_reset, // ✅ old key
        leaves_carry_forward:
          values.should_reset === "yes" ? String(values.carry_forward) : "0", // ✅ old key, stringified
      };

    await postData("/hrpayroll/save-client-leave-policy", payload);

      message.success(edit ? "Leave policy updated" : "Leave policy saved");
      dispatch(fetchClients());
      onClose();
    } catch (error) {
      console.error("Error saving policy:", error);
      message.error(error?.message || "Failed to save leave policy");
    }
  };

  return (
    <Modal
      title="Client Leave Policy"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={edit ? "Save" : "Save Leave Policy"}
      destroyOnClose
      width={550}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) =>
          required ? (
            <>
              {label} <span style={{ color: "#ff4d4f" }}>*</span>
            </>
          ) : (
            label
          )
        }
        onFinish={onFinish}
        style={{ maxHeight: "75vh", overflowY: "auto" }}
      >
        {/* Client */}
        <Form.Item
          label={
            <Row justify="space-between" align="middle">
              <Col>Client</Col>              
            </Row>
          }
          name="client_id"
          rules={[{ required: true, message: "Client is required" }]}
        >
          <Select placeholder="Select Client" showSearch>
            {clients.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Annual/Casual Leave Type */}
        <Form.Item
          label={isIndia ? "Casual Leave Type" : "Annual Leave Type"}
          name="annual_leave_type"
          rules={[{ required: true, message: "Leave type is required" }]}
        >
          <Select>
            <Option value="Calendar Days">Calendar Days</Option>
            <Option value="Working Days">Working Days</Option>
          </Select>
        </Form.Item>

        {/* Annual/Casual Leave Days */}
        <Form.Item
          label={isIndia ? "Casual Leave Days" : "Annual Leave Days"}
          name="annual_leave_days"
          rules={[{ required: true, message: "Leave days are required" }]}
        >
          <InputNumber min={1} max={365}
          style={{ width: "100%" }}
          onWheel={(e) => e.target.blur()}/>
        </Form.Item>

        {/* Sick Leave Days */}
        <Form.Item label="Sick Leave Days" name="sick_leave_days">
          <InputNumber min={0} max={365}
          style={{ width: "100%" }}
          onWheel={(e) => e.target.blur()}/>
        </Form.Item>

        {/* Flexi Leave Days */}
        <Form.Item label="Flexi Leave Days" name="flexi_leave_days">
          <InputNumber min={0} max={365}
          style={{ width: "100%" }}
          onWheel={(e) => e.target.blur()}/>
        </Form.Item>

        {/* Weekend Days */}
        <Form.Item
          label="Weekend Days"
          name="weekend_days"
          rules={[
            { required: true, message: "Select at least one weekend day" },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select weekend days"
            options={days.map((d) => ({ label: d.label, value: d.label }))}
          />
        </Form.Item>

        {/* Reset on New Year */}
        <Form.Item
          label="Should Accrued Leave Reset on New Year?"
          name="should_reset"
          rules={[{ required: true, message: "Please choose an option" }]}
        >
          <Radio.Group>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </Form.Item>

        {/* Carry Forward — Only visible if "Yes" is selected */}
        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) => prev.should_reset !== curr.should_reset}
        >
          {({ getFieldValue }) =>
            getFieldValue("should_reset") === "yes" ? (
              <Form.Item
                label="No. of Leaves to Carry Forward After Reset"
                name="carry_forward"
                rules={[
                  {
                    required: true,
                    message: "Please enter number of carry forward leaves",
                  },
                ]}
              >
                <InputNumber min={0} max={365} 
                style={{ width: "100%" }} 
                onClick={() => {
                  if (form.getFieldValue("carry_forward") === 0) {
                    form.setFieldValue("carry_forward", "");
                  }
                }}
                onWheel={(e) => e.target.blur()}/>
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ClientLeavePolicyModal;
